<template>
  <div class="page-container">
    <div class="landing-page">
      <!-- Top Navigation Bar -->
      <div class="top-nav">
        <div class="nav-left">
          <img
            src="@/assets/images/watercolor_logo.png"
            alt="ImmersiveLang Logo"
            class="nav-logo"
          >
          <h1 class="nav-title">
            ImmersiveLang
          </h1>
        </div>
        <div class="nav-buttons">
          <Button 
            label="Sign Up" 
            class="p-button-lg cta-button hero-button"
            style="height: 40px;"
            @click="navigateToRegister"
          />
          <Button 
            label="I Already Have an Account" 
            class="p-button-sm secondary-nav hero-button"
            style="height: 40px;"
            @click="navigateToLogin"
          />
        </div>
      </div>

      <!-- Hero Section -->
      <div class="hero-section">
        <!-- Reader image with annotation badges + chat video PiP -->
        <div class="hero-logo-container">
          <img
            src="/media/reader-demo-new.png"
            alt="ImmersiveLang document reader demo"
            class="hero-logo"
            loading="eager"
          >
          <div
            class="hero-annotation ann-left"
            style="bottom: 20%; --arrow-len: 200px;"
          >
            <div class="ann-badge">
              Read Your Documents
            </div>
            <div class="ann-connector" />
            <div class="ann-dot" />
          </div>
          <div
            class="hero-annotation ann-right"
            style="top: 1%; --arrow-len: 220px;"
          >
            <div class="ann-badge">
              Extract Vocabulary
            </div>
            <div class="ann-connector" />
            <div class="ann-dot" />
          </div>
          <div
            class="hero-annotation ann-right"
            style="top: 20%; --arrow-len: 180px;"
          >
            <div class="ann-multi-badges">
              <div class="ann-badge">
                Quick Vocabulary Check
              </div>
              <div class="ann-badge">
                Smart Summary
              </div>
              <div class="ann-badge">
                Q&amp;A Comprehension
              </div>
            </div>
            <div class="ann-connector" />
            <div class="ann-dot" />
          </div>
          <div
            class="hero-annotation ann-left"
            style="top: -1%; --arrow-len: 160px;"
          >
            <div class="ann-badge">
              Listen to Your Documents
            </div>
            <div class="ann-connector" />
            <div class="ann-dot" />
          </div>
          <!-- Chat video PiP — bottom-right corner -->
          <div class="hero-chat-pip">
            <img
              v-if="chatImagePath"
              :src="chatImagePath"
              class="hero-chat-image"
              alt="Chat Demo"
            >
            <div class="chat-ann-dot" />
            <div class="chat-ann-connector" />
            <div class="chat-ann-callout">
              Practice What You Read
            </div>
          </div>
        </div>

        <div class="hero-body">
          <!-- Left: Learning Cycle Diagram -->
          <LearningCycleCard class="learning-cycle-card" />

          <!-- Right: Title + CTA -->
          <div class="hero-text-col">
            <p class="hero-eyebrow">
              ImmersiveLang, your everyday companion
            </p>
            <h1
              v-if="showQuoteTitle"
              class="hero-title quote-title"
            >
              <div class="quote-line">
                <span class="fixed-text">Language is</span>
              </div>
              <div class="quote-line">
                <span class="quote-word">{{ currentQuoteWord }}</span>
              </div>
            </h1>
            <h1
              v-else
              class="hero-title quote-title"
            >
              <div class="quote-line">
                <span class="title-word">Learn</span>
                <span class="title-word">Languages,</span>
              </div>
              <div class="quote-line">
                <span class="title-word">Learn</span>
                <span class="title-word">Life</span>
              </div>
            </h1>
            <div class="hero-cta-group">
              <Button
                label="Sign Up for Free →"
                class="p-button-lg cta-button hero-button hero-primary-cta"
                @click="navigateToRegister"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Feature Sections -->
      <!-- Reading -->
      <div
        ref="featuresSection"
        class="feature-card"
      >
        <div class="slide-layout">
          <div class="slide-info">
            <h2 class="feature-title">
              <span class="feature-title-main">Read Smarter</span>
            </h2>
            <ul class="slide-feature-list">
              <li><span class="benefit-icon">✓</span> Upload PDFs, images &amp; web pages</li>
              <li><span class="benefit-icon">✓</span> One-click word definitions &amp; translations</li>
              <li><span class="benefit-icon">✓</span> AI-powered vocabulary extraction</li>
              <li><span class="benefit-icon">✓</span> Document summaries &amp; Q&amp;A</li>
            </ul>
          </div>
          <div class="slide-media">
            <img
              v-if="readerImagePath"
              :src="readerImagePath"
              class="slide-image"
              alt="Reader Demo"
            >
          </div>
        </div>
      </div>

      <!-- Flashcards -->
      <div class="feature-card">
        <div class="slide-layout slide-layout-reverse">
          <div class="slide-info">
            <h2 class="feature-title">
              <span class="feature-title-main">Master Every Word</span>
            </h2>
            <ul class="slide-feature-list">
              <li><span class="benefit-icon">✓</span> Save words directly from your reading</li>
              <li><span class="benefit-icon">✓</span> Adaptive spaced repetition flashcards</li>
              <li><span class="benefit-icon">✓</span> Track progress with stats</li>
            </ul>
          </div>
          <div class="slide-media">
            <div class="video-crop">
              <video
                v-if="vocabVideoPath"
                :src="vocabVideoPath"
                class="slide-video"
                autoplay
                muted
                loop
                preload="none"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Chat -->
      <div class="feature-card">
        <div class="slide-layout">
          <div class="slide-info">
            <h2 class="feature-title">
              <span class="feature-title-main">Chat & Practice what you've learnt</span>
            </h2>
            <ul class="slide-feature-list">
              <li><span class="benefit-icon">✓</span> Chat in custom AI scenarios generated from your documents</li>
              <li><span class="benefit-icon">✓</span> Get real-time, contextual language corrections</li>
              <li><span class="benefit-icon">✓</span> Enjoy effortless mobile access</li>
            </ul>
          </div>
          <div class="slide-media">
            <img
              v-if="chatImagePath"
              :src="chatImagePath"
              class="slide-image"
              alt="Chat Demo"
            >
          </div>
        </div>
      </div>

      <!-- Chrome Extension -->
      <div class="feature-card">
        <div class="slide-layout">
          <div class="slide-info">
            <h2 class="feature-title">
              <span class="feature-title-main">Read Anywhere with Chrome Extension</span>
            </h2>
            <ul class="slide-feature-list">
              <li><span class="benefit-icon">✓</span> Read and look up words directly on web pages</li>
              <li><span class="benefit-icon">✓</span> Save vocabulary while browsing</li>
              <li><span class="benefit-icon">✓</span> Sync words with your flashcards instantly</li>
              <li><span class="benefit-icon">✓</span> Keep your learning flow without switching tabs</li>
            </ul>
          </div>
          <div class="slide-media">
            <video
              v-if="extensionVideoPath"
              :src="extensionVideoPath"
              class="slide-video"
              autoplay
              muted
              loop
              preload="none"
            />
          </div>
          <button
            class="extension-button"
            type="button"
            @click="navigateToChromeStore"
          >
            <img
              src="/media/chrome_store.png"
              alt="Get it on Chrome Web Store"
              loading="lazy"
            >
          </button>
        </div>
      </div>

      <!-- Call to Action -->
      <div class="final-cta">
        <h2 class="cta-title">
          Ready to Transform Your Language Learning?
        </h2>
        <p class="cta-subtitle">
          Join other language learners who are mastering languages their way
        </p>
        <div class="final-buttons">
          <Button
            label="Sign Up for Free →"
            class="p-button-lg cta-button hero-button"
            @click="navigateToRegister"
          />
          <Button
            label="Sign In"
            class="p-button-lg cta-button secondary hero-button"
            @click="navigateToLogin"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { Button } from "primevue";
  import { CHROME_EXTENSION_URL } from "@/assets/constants/constants";
  import LearningCycleCard from "./LearningCycleCard.vue";

  export default {
    components: {
      Button,
      LearningCycleCard,
    },
    data() {
      return {
        vocabGalleryStep: 'Step 1',
        quoteWords: ['Culture', 'Connection', 'Opportunity', 'Adventure', 'Expression', 'Life'],
        currentQuoteIndex: 0,
        showQuoteTitle: true,
        activeFeature: 'reader',

      };
    },
    computed: {
      readerImagePath() {
        return process.env && process.env.BASE_URL
          ? process.env.BASE_URL + 'media/reader-v0.4.0.png'
          : null;
      },
      extensionVideoPath() {
        return process.env && process.env.BASE_URL
          ? process.env.BASE_URL + 'media/extension.mp4'
          : null;
      },
      vocabVideoPath() {
        return process.env && process.env.BASE_URL
          ? process.env.BASE_URL + 'media/demo-flashcards.mp4'
          : null;
      },
      chatImagePath() {

        const result = process.env && process.env.BASE_URL
          ? process.env.BASE_URL + 'media/chat-demo-image.png'
          : null;
        return result;
      },
      currentQuoteWord() {
        return this.quoteWords[this.currentQuoteIndex];
      },
    },
    mounted() {
      this.startVocabGallerySequence();
      this.startQuoteSequence();
    },
    beforeUnmount() {
    },
    methods: {
      navigateToRegister() {
        this.$router.push({ name: "RegisterForm" });
      },
      navigateToLogin() {
        this.$router.push({ name: "LoginForm" });
      },
      navigateToChromeStore() {
        window.open(CHROME_EXTENSION_URL, "_blank");
      },
      scrollToFeatures() {
        this.$refs.featuresSection?.scrollIntoView({ behavior: "smooth" });
      },
      startVocabGallerySequence() {
        setInterval(() => {
          if (this.vocabGalleryStep === 'Step 1') {
            this.vocabGalleryStep = 'Step 2';
          } else if (this.vocabGalleryStep === 'Step 2') {
            this.vocabGalleryStep = 'Step 3';
          } else {
            this.vocabGalleryStep = 'Step 1';
          }
        }, 5000);
      },
      handleVocabVideoEnded() {
        this.vocabGalleryStep = 'Step 3';
        setTimeout(() => {
          this.vocabGalleryStep = 'Step 1';
        }, 5000);
      },
      startQuoteSequence() {
        const interval = setInterval(() => {
          if (this.currentQuoteIndex < this.quoteWords.length - 1) {
            this.currentQuoteIndex += 1;
          } else {
            // All words shown, stop the loop and hide quote title
            this.showQuoteTitle = false;
            clearInterval(interval);
          }
        }, 1000);
      },

    },
  };
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.learning-cycle-card {
  --diagram-size: min(420px, calc(100vw - 50px));
  margin-top: 30px;
}

.page-container {
  width: 100%;
  min-height: 100vh;
  background-color: var(--color-bg);
  overflow-x: hidden;
}

.main-bullet-point {
  font-weight: bold;
  width: 110px;
  display: inline-block;
  font-size: 30px;
}

@media (max-width: 768px) {
  .main-bullet-point {
    font-size: 20px;
    width: 85px;
  }
}

@media (max-width: 599px) {
  .main-bullet-point {
    font-size: 17px;
    width: 75px;
  }

  .landing-page {
    padding: 0 0 60px 0 !important;
    max-width: 1200px;
    margin: 0;
  }

  .feature-card {
    margin: 0 16px !important;
    overflow: hidden;
  }

  .final-cta {
    margin: 0 16px !important;
  }
}

/* Top Navigation */
.top-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 40px;
  background: linear-gradient(135deg, var(--color-aurora-end) 0%, var(--color-aurora-start) 100%);;
  border-bottom: none;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: var(--shadow-md);
}

.nav-left {
  display: flex;
  align-items: center;
  gap: 12px;
  overflow: hidden;
}

.nav-logo {
  width: 80px;
  overflow: hidden;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
}

.nav-title {
  font-size: 20px;
  font-weight: 800;
  color: var(--color-text-on-dark);
  margin: 0;
  font-family: var(--font-family);
}

.nav-buttons {
  display: flex;
  gap: 12px;
}

.nav-button {
  padding: 8px 20px !important;
  font-size: 13px;
  border-radius: 6px;
  font-weight: 600;
}

.primary-nav {
  background: var(--color-btn-primary-bg) !important;
  border: 1.5px solid var(--color-btn-primary-border) !important;
  color: var(--color-btn-primary-text) !important;
}

.primary-nav:hover {
  background: var(--color-btn-primary-hover) !important;
  transform: translateY(-1px);
}

.secondary-nav {
  background: rgba(255, 255, 255, 0.12) !important;
  color: var(--color-text-on-dark) !important;
  border: 1.5px solid rgba(255, 255, 255, 0.45) !important;
}

.secondary-nav:hover {
  background: rgba(255, 255, 255, 0.22) !important;
}

@media (max-width: 768px) {
  .top-nav {
    padding: 12px 16px;
    flex-direction: column;
    gap: 12px;
  }

  .nav-buttons {
    width: 100%;
    gap: 8px;
  }

  .nav-button {
    flex: 1;
    padding: 8px 12px !important;
    font-size: 12px;
  }
}

/* Hero Section */
.hero-section {
  text-align: center;
  padding: 35px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  animation: fadeInUp 0.8s ease-out;
}

.hero-body {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 48px;
  width: 100%;
}

.hero-text-col {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 16px;
  flex: 1;
  max-width: 480px;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hero-logo-container {
  position: relative;
  display: inline-block;
}

.hero-logo {
  height: 400px;
  border-radius: 16px;
  box-shadow: 0 0 0 3px var(--color-aurora-start), 0 8px 32px rgba(69, 74, 170, 0.25);
  display: block;
}

.hero-annotation {
  position: absolute;
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 5;
}

.ann-left {
  left: -370px;
  flex-direction: row;
}

.ann-right {
  right: -380px;
  flex-direction: row-reverse;
}

.ann-multi-badges {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
}

.ann-badge {
  background: var(--gradient-aurora);
  color: #fff;
  font-size: 16px;
  font-weight: 800;
  padding: 8px 16px;
  border-radius: 24px;
  white-space: nowrap;
  box-shadow: 0 4px 20px rgba(69, 74, 170, 0.35), 0 1px 4px rgba(0, 0, 0, 0.1);
  border: none;
  letter-spacing: 0.3px;
  font-family: var(--font-family);
}

.ann-connector {
  width: var(--arrow-len, 48px);
  height: 2px;
  background: var(--color-aurora-end);
  position: relative;
  flex-shrink: 0;
}

.ann-left .ann-connector::after {
  content: '';
  position: absolute;
  right: -6px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 7px solid var(--color-aurora-end);
}

.ann-right .ann-connector::after {
  content: '';
  position: absolute;
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-right: 7px solid var(--color-aurora-end);
}

.ann-dot {
  width: 12px;
  height: 12px;
  background: var(--color-aurora-end);
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 0 0 3px rgba(229, 171, 114, 0.35);
  flex-shrink: 0;
}

@media (max-width: 900px) {
  .hero-annotation {
    display: none;
  }
}

.hero-title {
  font-size: 60px;
  font-weight: 900;
  margin: 0;
  line-height: 1.1;
  font-family: var(--font-family);
  background: linear-gradient(135deg, var(--color-aurora-start) 0%, var(--color-aurora-end) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

@keyframes colorShift {
  0%, 100% {
    filter: hue-rotate(0deg);
  }
  50% {
    filter: hue-rotate(20deg);
  }
}

.hero-title.secondary-title {
  font-size: 48px;
  font-weight: 700;
  background: linear-gradient(135deg, var(--color-aurora-start) 0%, var(--color-aurora-end) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 16px 0 0 0;
}

/* Language Quote Section */
.language-quote {
  text-align: center;
  padding: 40px 20px;
  margin: 20px 0;
}

.quote-text {
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin: 0;
  font-family: "Franklin Gothic Medium", "Arial Narrow", Arial, sans-serif;
  line-height: 1.4;
}

.quote-word {
  color: #c77ddb;
  font-weight: 800;
  display: inline-block;
  min-width: 120px;
  animation: wordFadeInOut 3s ease-in-out infinite;
}

.hero-title.quote-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.quote-line {
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-title .quote-word {
  min-width: 280px;
  width: 350px;
  background: linear-gradient(135deg, var(--color-aurora-start) 0%, var(--color-aurora-end) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: wordFadeInOut 3s ease-in-out infinite;
  text-align: center;
}

.hero-title .fixed-text {
  flex: 0 0 auto;
  white-space: nowrap;
  background: linear-gradient(135deg, var(--color-aurora-start) 0%, var(--color-aurora-end) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

@keyframes wordFadeInOut {
  0% {
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

@media (max-width: 768px) {
  .language-quote {
    padding: 30px 16px;
    margin: 16px 0;
  }

  .quote-text {
    font-size: 24px;
  }
}

@media (max-width: 599px) {
  .language-quote {
    padding: 20px 12px;
    margin: 12px 0;
  }

  .quote-text {
    font-size: 20px;
  }
}

.title-word {
  display: inline-block;
  animation: titleWordIn 0.5s ease-out backwards;
  margin: 0 8px;
}

.quote-line:nth-child(1) .title-word:nth-child(1) { animation-delay: 0s; }
.quote-line:nth-child(1) .title-word:nth-child(2) { animation-delay: 0.12s; }
.quote-line:nth-child(2) .title-word:nth-child(1) { animation-delay: 0.24s; }
.quote-line:nth-child(2) .title-word:nth-child(2) { animation-delay: 0.36s; }

@keyframes titleWordIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.hero-subtitle {
  font-size: 18px;
  color: var(--color-text-secondary);
  max-width: 600px;
  line-height: 1.6;
  margin: 0;
  font-weight: bold;
  font-family: var(--font-family);
}

.highlight-text {
  color: var(--color-primary-mid);
  font-weight: 700;
}

.hero-buttons {
  display: flex;
  gap: 16px;
  margin-top: 12px;
  flex-wrap: wrap;
  justify-content: center;
}

.hero-button {
  padding: 16px 44px !important;
  font-size: 16px;
  border-radius: 8px;
  font-weight: 700;
}

@media (max-width: 768px) {
  .hero-section {
    padding: 40px 20px;
  }

  .hero-body {
    flex-direction: column;
    align-items: center;
    gap: 32px;
  }

  .hero-text-col {
    align-items: center;
    text-align: center;
    max-width: 100%;
  }

  .hero-logo-container {
    width: 100%;
  }

  .hero-logo {
    height: auto;
    width: 100%;
    max-width: 100%;
  }

  .hero-title {
    font-size: 42px;
  }

  .hero-title.secondary-title {
    font-size: 32px;
  }

  .hero-title.quote-title {
    gap: 4px;
  }

  .hero-title .quote-word {
    width: 200px;
    min-width: 200px;
  }

  .hero-subtitle {
    font-size: 15px;
  }

  .hero-button {
    padding: 12px 32px !important;
    font-size: 14px;
    width: 100%;
  }
}

/* Feature Sections */
.landing-page {
  display: flex;
  flex-direction: column;
  gap: 48px;
  padding: 0 5px 60px 5px;
  max-width: 1200px;
  margin: 0 auto;
}

.feature-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 48px;
  row-gap: 20px;
  animation: fadeInUp 0.8s ease-out;
}

.reading-section {
  background: white;
  padding: 40px;
  border-radius: 20px;
  border: 1px solid rgba(27, 30, 100, 0.10);
  grid-template-areas: "top media" "bottom media";
  transition: border-color var(--transition-base);
}

.vocab-section {
  background: white;
  padding: 40px;
  border-radius: 20px;
  border: 1px solid rgba(27, 30, 100, 0.10);
  grid-template-areas: "media top" "media bottom";
  transition: border-color var(--transition-base);
}

.extension-section {
  background: white;
  padding: 40px;
  border-radius: 20px;
  border: 1px solid rgba(27, 30, 100, 0.10);
  grid-template-areas: "top media" "bottom media";
  transition: border-color var(--transition-base);
}

.reading-section:hover,
.vocab-section:hover,
.extension-section:hover {
  border-color: var(--color-primary-mid);
}

.feature-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.feature-content-top {
  display: flex;
  flex-direction: column;
  gap: 8px;
  grid-area: top;
}

.feature-content-bottom {
  display: flex;
  flex-direction: column;
  gap: 20px;
  grid-area: bottom;
}

.reading-section .feature-media,
.vocab-section .feature-media,
.extension-section .feature-media {
  grid-area: media;
  align-self: center;
}

.feature-title {
  font-size: 28px;
  font-weight: 800;
  margin: 0;
  color: #333;
  font-family: "Franklin Gothic Medium", "Arial Narrow", Arial, sans-serif;
}

.feature-title-main {
  color: var(--color-primary-mid);
}

.feature-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  width: fit-content;
  font-family: "Franklin Gothic Medium", "Arial Narrow", Arial, sans-serif;
}

.reading-tag {
  background: var(--color-secondary-tint);
  color: var(--color-primary);
}

.vocab-tag {
  background: var(--color-secondary-tint);
  color: var(--color-primary);
}

.extension-tag {
  background: var(--color-secondary-tint);
  color: var(--color-primary);
}

.feature-text {
  font-size: 16px;
  line-height: 1.7;
  color: var(--color-text-secondary);
  margin: 0;
}

.feature-benefits {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.benefit-item {
  font-size: 14px;
  color: var(--color-text-primary);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--color-surface-raised);
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid rgba(27, 30, 100, 0.06);
}

.benefit-icon {
  width: 22px;
  height: 22px;
  min-width: 22px;
  background: var(--gradient-aurora);
  color: white;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
}

.feature-media {
  display: flex;
  align-items: center;
  justify-content: center;
}

.vocab-gallery {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: -30px;
  position: relative;
  height: 350px;
}

.gallery-step-indicator {
  position: absolute;
  top: 10px;
  left: 10px;
  background: #5f2ada;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  z-index: 30;
}

.gallery-top-center {
  display: flex;
  justify-content: center;
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;
}

@media (max-width: 599px) {
  .gallery-top-center .gallery-image:not(.gallery-image-center) {
    display: none;
  }
}

.gallery-image-center {
  width: 100%;
  max-width: 500px;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  object-fit: cover;
}

.gallery-video {
  width: 100%;
  max-width: 450px;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.gallery-bottom-row {
  display: flex;
  gap: 40px;
  justify-content: center;
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

@media (max-width: 599px) {
  .gallery-bottom-row {
    gap: 0;
    width: 100%;
    position: relative;
    height: 350px;
  }

  .gallery-image {
    max-width: 280px;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: 80px;
  }

  .gallery-image-1 {
    z-index: 2;
  }

  .gallery-image-2 {
    z-index: 1;
  }
}

.gallery-step-text {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 30;
  width: 100%;
  text-align: center;
}

.step-text {
  font-size: 16px;
  font-weight: 600;
  color: #5f2ada;
  margin: 0;
  animation: fadeInOut 0.5s ease-in-out;
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.gallery-image {
  width: 100%;
  max-width: 350px;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  object-fit: cover;
  opacity: 1;
  animation: cycleImages 6s infinite;
}

.gallery-image-1 {
  animation-delay: 0s;
}

.gallery-image-2 {
  animation-delay: 0.7s;
}

.gallery-image-3 {
  animation-delay: 0s;
}

@media (max-width: 599px) {
  .gallery-image-1 {
    animation: showThenHide 3s infinite;
    animation-delay: 0s;
  }

  .gallery-image-2 {
    animation: showThenHide 3s infinite;
    animation-delay: 1.5s;
  }
}

@keyframes showThenHide {
  0% {
    opacity: 1;
  }
  49.9% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

@keyframes cycleImages {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 1;
  }
  55% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

@keyframes cycleImageProfile {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0;
  }
  55% {
    opacity: 1;
  }
  100% {
    opacity: 1;
  }
}

.slide-video {
  width: 100%;
  max-width: 100%;
  height: auto;
  border-radius: 14px;
  display: block;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}


.slide-image {
  max-height: 380px;
  max-width: 100%;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.reading-section .slide-image {
  box-shadow: 0 0 0 2px var(--color-aurora-start), 0 0 0 6px rgba(69, 74, 170, 0.20), 0 16px 48px rgba(69, 74, 170, 0.15);
}

.reading-section .slide-image:hover {
  transform: translateY(-4px);
  box-shadow: 0 0 0 2px var(--color-aurora-start), 0 0 0 10px rgba(69, 74, 170, 0.25), 0 24px 60px rgba(69, 74, 170, 0.20);
}

.extension-section .slide-video {
  box-shadow: 0 0 0 2px var(--color-aurora-start), 0 0 0 6px rgba(69, 74, 170, 0.20), 0 16px 48px rgba(69, 74, 170, 0.15);
}

.extension-section .slide-video:hover {
  transform: translateY(-4px);
  box-shadow: 0 0 0 2px var(--color-aurora-start), 0 0 0 10px rgba(69, 74, 170, 0.25), 0 24px 60px rgba(69, 74, 170, 0.20);
}

.video-crop {
  width: 100%;
  overflow: hidden;
  border-radius: 14px;
}

.video-crop .slide-video {
  width: 100%;
  height: 600px;
  display: block;
  margin-top: -100px;
  margin-bottom: -100px;
  border-radius: 0;
}

.feature-image {
  width: 100%;
  max-width: 350px;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.feature-image-large {
  width: 100%;
  max-width: 400px;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

@keyframes buttonShimmer {
  0%, 55%, 100% {
    transform: translateX(-180%) skewX(-20deg);
    opacity: 0;
  }
  60% {
    opacity: 1;
  }
  85% {
    transform: translateX(220%) skewX(-20deg);
    opacity: 0;
  }
}

.extension-button {
  align-self: flex-start;
  margin-top: 12px;
  border: none;
  padding: 0;
  cursor: pointer;
  border-radius: 14px;
  display: inline-block;
  background: none;
  position: relative;
  overflow: hidden;
  transition: transform 0.25s ease;
  width: fit-content;
}

.extension-button::before {
  content: '';
  position: absolute;
  top: -10%;
  left: 0;
  width: 55%;
  height: 120%;
  background: linear-gradient(
    105deg,
    transparent 10%,
    rgba(199, 125, 219, 0.4) 35%,
    rgba(155, 63, 214, 0.55) 50%,
    rgba(74, 144, 226, 0.4) 65%,
    transparent 90%
  );
  transform: translateX(-180%) skewX(-20deg);
  animation: buttonShimmer 5s ease-in-out infinite;
  z-index: 2;
  pointer-events: none;
  border-radius: 12px;
}

.extension-button img {
  display: block;
  border-radius: 12px;
  position: relative;
  z-index: 0;
  border: 2px solid gray;
  height: 80px;
}

.extension-button:hover {
  transform: scale(1.04);
}

.extension-button:hover::before {
  animation-play-state: paused;
}

.extension-button:active {
  transform: scale(0.97);
}

/* Final CTA */
.final-cta {
  text-align: center;
  background: var(--color-primary);
  padding: 60px 40px;
  border-radius: 16px;
  color: white;
}

.cta-title {
  font-size: 36px;
  font-weight: 800;
  margin: 0 0 12px 0;
  font-family: "Franklin Gothic Medium", "Arial Narrow", Arial, sans-serif;
}

.cta-subtitle {
  font-size: 18px;
  margin: 0 0 32px 0;
  opacity: 0.95;
  line-height: 1.6;
}

.final-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-button {
  padding: 14px 48px !important;
  font-size: 16px;
  border-radius: 8px;
  font-family: var(--font-family);
  font-weight: 700;
  background: var(--color-btn-primary-bg) !important;
  border: 1.5px solid var(--color-btn-primary-border) !important;
  color: var(--color-btn-primary-text) !important;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-sm);
}

.cta-button:hover {
  background: var(--color-btn-primary-hover) !important;
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.cta-button.secondary {
  background: var(--color-btn-secondary-bg) !important;
  color: var(--color-btn-secondary-text) !important;
  border: none !important;
  box-shadow: var(--shadow-sm);
}

.cta-button.secondary:hover {
  background: var(--color-btn-secondary-hover) !important;
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.final-cta .cta-button.secondary {
  background: rgba(255, 255, 255, 0.15) !important;
  color: var(--color-text-on-dark) !important;
  border: 1.5px solid rgba(255, 255, 255, 0.45) !important;
}

.final-cta .cta-button.secondary:hover {
  background: rgba(255, 255, 255, 0.25) !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .app-content-container.landing-page {
    gap: 36px;
    padding: 0 20px 40px 20px;
  }

  .feature-section {
    grid-template-columns: 1fr;
    row-gap: 20px;
    column-gap: 0;
  }

  .reading-section,
  .vocab-section,
  .extension-section {
    grid-template-areas:
      "top"
      "media"
      "bottom";
    grid-template-rows: auto;
    padding: 24px;
  }

  .feature-title {
    font-size: 22px;
  }

  .feature-text {
    font-size: 14px;
  }

  .video-placeholder {
    padding: 60px 20px;
    min-height: 250px;
  }

  .feature-image,
  .feature-image-large {
    max-width: 100%;
  }

  .extension-button {
    align-self: center;
    width: fit-content;
  }

  .final-cta {
    padding: 40px 24px;
  }

  .cta-title {
    font-size: 26px;
  }

  .cta-subtitle {
    font-size: 16px;
  }

  .final-buttons {
    flex-direction: column;
  }

  .final-buttons .cta-button {
    width: 100%;
  }
}

@media (max-width: 599px) {
  .top-nav {
    padding: 12px 12px;
  }

  .nav-button {
    padding: 6px 10px !important;
    font-size: 11px;
  }

  .hero-section {
    padding: 30px 16px;
    gap: 16px;
  }

  .hero-title {
    font-size: 32px;
  }

  .hero-title.secondary-title {
    font-size: 26px;
  }

  .hero-title.quote-title {
    flex-direction: column;
    gap: 4px;
  }

  .hero-title .fixed-text {
    white-space: normal;
  }

  .hero-title .quote-word {
    width: 150px;
    min-width: 200px;
  }

  .hero-subtitle {
    font-size: 14px;
  }

  .hero-buttons {
    gap: 10px;
  }

  .hero-button {
    padding: 10px 24px !important;
    font-size: 13px;
  }

  .app-content-container.landing-page {
    gap: 28px;
    padding: 0 16px 32px 16px;
  }

  .feature-title {
    font-size: 18px;
  }

  .feature-text {
    font-size: 13px;
  }

  .benefit-item {
    font-size: 13px;
  }

  .final-cta {
    padding: 32px 16px;
  }

  .cta-title {
    font-size: 20px;
  }

  .cta-subtitle {
    font-size: 14px;
  }
}

/* =============================================
   Hero — scanner-friendly additions
   ============================================= */
.hero-eyebrow {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary-mid);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0 0 var(--space-2) 0;
}

.hero-bullets {
  list-style: none;
  padding: 0;
  margin: var(--space-4) auto var(--space-6) auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  text-align: left;
  max-width: 680px;
}

.hero-bullets li {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-size: var(--font-size-md);
  color: var(--color-text-primary);
  line-height: var(--line-height-base);
  font-family: var(--font-family);
}

.hero-bullets strong {
  color: var(--color-primary-mid);
}

.bullet-check {
  width: 22px;
  height: 22px;
  min-width: 22px;
  background: var(--gradient-aurora);
  color: white;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
}

.hero-cta-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
}

.hero-primary-cta {
  padding: 18px 60px !important;
  font-size: 18px !important;
  letter-spacing: 0.02em;
  min-width: 280px;
  margin-left: 80px;
  margin-top: 50px;
}

.scroll-arrow-icon {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg);
  animation: bounce-arrow 1.8s ease-in-out infinite;
}

@keyframes bounce-arrow {
  0%, 100% { transform: rotate(45deg) translateY(0); }
  50% { transform: rotate(45deg) translateY(5px); }
}

/* =============================================
   Chat video PiP — overlaid on reader image
   ============================================= */
.hero-chat-pip {
  position: absolute;
  bottom: -60px;
  right: -378px;
  width: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0;
  z-index: 10;
}

.hero-chat-pip img {
  width: 140px;
  flex-shrink: 0;
  border-radius: var(--radius-lg);
  display: block;
  box-shadow:
    0 0 0 2px var(--color-aurora-start),
    0 0 0 6px rgba(69, 74, 170, 0.18),
    0 8px 24px rgba(27, 30, 100, 0.22);
}

.chat-ann-dot {
  width: 12px;
  height: 12px;
  min-width: 12px;
  background: var(--color-aurora-end);
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 0 0 3px rgba(229, 171, 114, 0.35);
  flex-shrink: 0;
  margin-left: 6px;
}

.chat-ann-connector {
  width: 72px;
  height: 2px;
  background: var(--color-aurora-end);
  position: relative;
  flex-shrink: 0;
}

.chat-ann-connector::after {
  content: '';
  position: absolute;
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-right: 7px solid var(--color-aurora-end);
}

.chat-ann-callout {
  background: var(--gradient-aurora);
  color: #fff;
  font-weight: 800;
  padding: 6px 14px;
  border-radius: 20px;
  white-space: normal;
  font-family: var(--font-family);
  letter-spacing: 0.2px;
  box-shadow: 0 4px 16px rgba(69, 74, 170, 0.30), 0 1px 4px rgba(0, 0, 0, 0.10);
  align-self: center;
  margin-left: 8px;
}

/* =============================================
   Feature Cards (standalone sections)
   ============================================= */
.feature-card {
  background: var(--color-surface);
  border: 1px solid rgba(27, 30, 100, 0.10);
  border-radius: 20px;
  padding: var(--space-10) var(--space-8);
  transition: border-color var(--transition-base);
}

.feature-card:hover {
  border-color: var(--color-primary-mid);
}

/* =============================================
   Feature Slider (kept for reference)
   ============================================= */
.feature-slider {
  background: var(--color-surface);
  border: 1px solid rgba(27, 30, 100, 0.10);
  border-radius: 20px;
  overflow: hidden;
  transition: border-color var(--transition-base);
}

.feature-slider:hover {
  border-color: var(--color-primary-mid);
}

.slider-tab-bar {
  display: flex;
  gap: var(--space-2);
  background: var(--color-surface-raised);
  border-bottom: 1px solid rgba(27, 30, 100, 0.08);
  padding: var(--space-3) var(--space-4);
}

.slider-tab {
  padding: var(--space-2) var(--space-5);
  border-radius: var(--radius-md);
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  font-family: var(--font-family);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);
}

.slider-tab:hover {
  background: var(--color-btn-secondary-bg);
  color: var(--color-primary);
}

.slider-tab-active {
  background: var(--color-primary) !important;
  color: var(--color-text-on-dark) !important;
}

.slider-panel {
  padding: var(--space-10) var(--space-8);
}

.slide-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-8);
  align-items: center;
}

.slide-layout-reverse {
  direction: rtl;
}

.slide-layout-reverse > * {
  direction: ltr;
}

.slide-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.slide-feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  text-align: left;
}

.slide-feature-list li {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  color: var(--color-text-secondary);
  font-family: var(--font-family);
}

.slide-media {
  display: flex;
  align-items: center;
  justify-content: center;
}

.slide-chat-placeholder {
  width: 100%;
  min-height: 300px;
  background: var(--color-surface-raised);
  border: 1.5px dashed rgba(27, 30, 100, 0.15);
  border-radius: var(--radius-lg);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
}

.chat-placeholder-emoji {
  font-size: 52px;
  opacity: 0.45;
}

.chat-placeholder-text {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  margin: 0;
}

.chat-tag {
  background: var(--color-secondary-tint);
  color: var(--color-primary);
}

@media (max-width: 768px) {
  .slide-layout {
    grid-template-columns: 1fr;
  }

  .slide-layout-reverse {
    direction: ltr;
  }

  .slider-panel {
    padding: var(--space-6) var(--space-4);
  }

  .slider-tab {
    font-size: var(--font-size-sm);
    padding: var(--space-2) var(--space-3);
  }

  .hero-bullets {
    max-width: 100%;
  }

  .hero-primary-cta {
    min-width: unset;
    width: 100%;
    margin-left: 0 !important;
    margin-top:  0 !important;
  }

  .hero-chat-pip {
    display: none;
  }
}

</style>
